import React, { Fragment, useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Table from "react-bootstrap/Table";
import { PaginationControl } from "react-bootstrap-pagination-control";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import { ButtonBulkDelete } from "../components/ShiftShiftButtonActionsElements";
import { AxisDate, AxisDateWithDay, AxisHoursWork } from "../util/AxisDateTime";
import AxisSpinner from "../components/AxisSpinner";
import { ShiftTypeString } from "../util/ShiftOptionsSelection";
import DropDownTrustSelection from "../components/DropDownTrustSelection";
import DropDownTimesheetWeekSelection from "../components/DropDownTimesheetWeekSelection";
import UserSelection from "../components/UserSelection";
import { ShiftLink, UserLink } from "../util/Links";
import DropDownFetchCount from "../components/DropDownFetchCount";
import DropDownRoleSelection from "../components/DropDownRoleSelection";
import XLSX from "xlsx";
import { PageTitleController } from "../components/PageTitleController";
import { BsCalendar3RangeFill, BsFillClipboard2DataFill } from "react-icons/bs";
import { ShiftTableDetailModal } from "../modals/ShiftTableDetailModal";
import { Container } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { TimesheetStatusTextIcon } from "../components/TimesheetStatusTextIcon";
import BulkDeleteModal from "../modals/BulkDeleteModal";

const TimesheetTable = ({
  defaultUser = null,
  needTitle = true,
  needTabs = true,
}) => {
  const historyKey = "TimesheetTable";
  let { state } = useLocation();
  if (state?.hasOwnProperty("redirectKey")) localStorage.removeItem("history");
  const getKeyStartingState = () => {
    if (state === null || state === undefined) return "ALL";
    if (state.hasOwnProperty("redirectKey")) return state.redirectKey;
    return "ALL";
  };

  let history = localStorage.key("history")
    ? JSON.parse(localStorage.getItem("history"))
    : null;
  if (history !== null && historyKey in history)
    history = JSON.parse(history[historyKey]);
  else history = null;

  if (
    history !== null &&
    history?.lastUpdated !== undefined &&
    new Date() - new Date(history.lastUpdated) > 600000
  ) {
    localStorage.removeItem("history");
    history = null;
  }

  const { authAxios } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState([]);
  const [offset, setOffset] = useState(history ? history?.offset : 0);
  const [totalShifts, setTotalShifts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [fetchCount, setFetchCount] = useState(
    history ? history?.fetchCount : 25,
  );
  const [key, setKey] = useState(
    history ? history?.key : getKeyStartingState(),
  );
  const [timesheetWeek, setTimesheetWeek] = useState(
    history ? history?.timesheetWeek : null,
  );
  const [user, setUser] = useState(history ? history?.user : null);
  const [sortKey, setSortKey] = useState(
    history ? history?.sortKey : "created",
  );
  const [sortDirection, setSortDirection] = useState(
    history ? history?.sortDirection : "dsc",
  );
  const tableRef = useRef();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectDetail, setSelectDetail] = useState(null);
  const [trust, setTrust] = useState(history ? history?.trust : null);
  const [role, setRole] = useState(history ? history?.role : null);
  const [hasPermission, setHasPermission] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [disableClear, setDisableClear] = useState(false);
  const [bulkCreateLoading, setBulkCreateLoading] = useState(false);
  const [bulkCreateProgress, setBulkCreateProgress] = useState(null);
  
  // Refs for dropdown components
  const trustRef = useRef();
  const roleRef = useRef();
  const userRef = useRef();
  const timesheetWeekRef = useRef();
  const fetchCountRef = useRef();

  useEffect(() => {
    document.title = "Timesheets";
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: {},
      };

      const response = await authAxios().get(
        "/api/backoffice/users/current_user",
        config,
      );

      var role = response?.data?.role;
      setHasPermission(role === "ADMIN" || role === "SUPERADMIN");
    } catch (error) {
      console.log("Error catch");
      console.log(error);
    }
  };

  useEffect(() => {
    if (defaultUser != null) setUser(defaultUser);
  }, [defaultUser]);

  const updateHistoryJSON = () => {
    if (defaultUser !== null) return;

    var saveStates = {};
    saveStates[historyKey] = JSON.stringify({
      key: key,
      trust: trust,
      role: role,
      sortKey: sortKey,
      sortDirection: sortDirection,
      offset: offset,
      user: user,
      timesheetWeek: timesheetWeek,
      lastUpdated: new Date(),
      fetchCount: fetchCount,
    });
    localStorage.setItem("history", JSON.stringify(saveStates));
  };

  useEffect(() => {
    paginateShifts();
    updateHistoryJSON();
  }, [
    offset,
    key,
    timesheetWeek,
    user,
    trust,
    role,
    fetchCount,
    sortKey,
    sortDirection,
  ]);

  const clearFilters = () => {
    // Reset state
    setTrust(null);
    setRole(null);
    setUser(null);
    setTimesheetWeek(null);
    setSortKey("created");
    setSortDirection("dsc");
    
    // Reset UI components using refs
    if (trustRef.current) trustRef.current.reset();
    if (roleRef.current) roleRef.current.reset();
    if (userRef.current) userRef.current.reset();
    if (timesheetWeekRef.current) timesheetWeekRef.current.reset();
    if (fetchCountRef.current) fetchCountRef.current.reset();
  };

  const closeDetailModal = () => {
    setDetailModalVisible(false);
    setSelectDetail(null);
  };

  const openDetailModal = (shift) => {
    setSelectDetail(shift);
    setDetailModalVisible(true);
  };

  const paginateShifts = async () => {
    try {
      setLoading(true);

      var params = {
        status: key,
      };
      if (timesheetWeek !== null) {
        params["timesheetWeek"] = timesheetWeek?.id;
      }
      if (trust !== null) {
        params["trust_id"] = trust?.id;
      }
      if (role !== null) {
        params["role"] = role;
      }
      if (user !== null) {
        params["user_id"] = user?.id;
      } else if (defaultUser !== null) {
        params["user_id"] = defaultUser?.id;
      }

      const pageSettings = {
        direction: sortDirection,
        key: sortKey,
        page: offset,
        elementPerPage: fetchCount,
      };
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: params,
      };

      const page = await authAxios().post(
        "/api/backoffice/timesheet/paging",
        pageSettings,
        config,
      );
      setLoading(false);

      if (page.data.content.length === 0) {
        if (dataSource.length === 0) {
          setTotalShifts(0);
        }
        setDataSource([]);
        setTotalShifts(0);
        return;
      }

      var content = page.data.content.map((shift) => {
        shift["selected"] = false;
        return shift;
      });

      setDataSource(content);
      setTotalShifts(page.data.totalElements);
      var length = Math.ceil(page.data.totalElements / fetchCount);
      setTotalPages(length);
    } catch (error) {
      setLoading(false);
      console.log("Error catch");
      console.log(error);
      console.log(error.stack);
    }
    setDisableClear(false);
  };

  const exportTable = async () => {
    const wb = XLSX.utils.table_to_book(tableRef.current);
    XLSX.writeFile(wb, "TimesheetTable.xlsx");
  };

  const isActiveTable = (key) => {
    return key === "MISSING";
  };

  const isApprovedTab = () => key === "APPROVED";

  const selectedCount = dataSource.filter((s) => s.selected).length;

  const bulkCreateInvoices = async () => {
    const selected = dataSource.filter((s) => s.selected);
    if (selected.length === 0) return;
    try {
      setBulkCreateLoading(true);
      setBulkCreateProgress({ current: 0, total: selected.length });
      for (let i = 0; i < selected.length; i++) {
        setBulkCreateProgress({ current: i + 1, total: selected.length });
        try {
          await authAxios().post(
            `/api/backoffice/invoice/create_for_shift?booked_shift_id=${selected[i].bookedShiftId}`,
          );
        } catch (e) {
          if (e.response?.status !== 409) console.log(e);
        }
      }
      await paginateShifts();
    } catch (error) {
      console.log(error);
      alert("Failed to bulk create invoices");
    } finally {
      setBulkCreateLoading(false);
      setBulkCreateProgress(null);
    }
  };

  const Checkbox = ({ isChecked, checkHandler, index }) => {
    return (
      <div>
        <input
          type="checkbox"
          id={`checkbox-${index}`}
          checked={isChecked}
          onChange={checkHandler}
        />
      </div>
    );
  };

  const toggleSelected = (index) => {
    setDataSource(
      dataSource.map((shift, currentIndex) =>
        currentIndex === index
          ? { ...shift, selected: !shift.selected }
          : shift,
      ),
    );
  };

  const allSelected = dataSource.length > 0 && dataSource.every((s) => s.selected);

  const toggleSelectAll = () => {
    setDataSource(dataSource.map((s) => ({ ...s, selected: !allSelected })));
  };

  const SpinnerOrData = (loading) => {
    if (loading) return <AxisSpinner loading={loading} />;
    return (
      <Fragment>
        <Table striped bordered hover ref={tableRef}>
          <thead>
            <tr>
              {(isActiveTable(key) || isApprovedTab()) ? (
                <th style={{ width: "36px" }}>
                  {isApprovedTab() ? (
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                    />
                  ) : null}
                </th>
              ) : null}
              <th>Reference</th>
              <th>Name</th>
              <th>Trust</th>
              <th>Facility</th>
              <th>Ward</th>
              <th>Grade</th>
              <th>Type</th>
              <th>Status</th>
              <th>Date</th>
              <th>Ts Week</th>
              <th>Hours Worked</th>
              <th>Ts Status</th>
            </tr>
          </thead>
          <tbody>
            {dataSource.map((shift, index) => (
              <tr key={shift?.id}>
                {(isActiveTable(key) || isApprovedTab()) ? (
                  <td>
                    <Checkbox
                      key={shift?.id}
                      isChecked={shift?.selected}
                      checkHandler={() => toggleSelected(index)}
                      index={index}
                    />
                  </td>
                ) : null}
                <td onClick={() => openDetailModal(shift)}>
                  {ShiftLink(shift)}
                </td>
                <td onClick={() => openDetailModal(shift)}>
                  {UserLink(shift?.user)}
                </td>
                <td onClick={() => openDetailModal(shift)}>
                  {shift?.trust?.shortName}
                </td>
                <td onClick={() => openDetailModal(shift)}>
                  {shift?.facility?.name}
                </td>
                <td onClick={() => openDetailModal(shift)}>
                  {shift?.ward?.identifier}
                </td>
                <td onClick={() => openDetailModal(shift)}>{shift?.grade}</td>
                <td onClick={() => openDetailModal(shift)}>
                  {ShiftTypeString(shift?.shiftType)}
                </td>
                <td
                  className={"text-capitalize"}
                  onClick={() => openDetailModal(shift)}
                >
                  {shift?.status.toLowerCase()}
                </td>
                <td onClick={() => openDetailModal(shift)}>
                  {AxisDateWithDay(shift?.start)}
                </td>
                <td onClick={() => openDetailModal(shift)}>
                  {AxisDate(shift?.timesheetWeek)}
                </td>
                <td onClick={() => openDetailModal(shift)}>
                  {AxisHoursWork(shift?.timesheet?.hoursWorked)}
                </td>
                <td onClick={() => openDetailModal(shift)}>
                  <TimesheetStatusTextIcon
                    status={shift?.timesheet?.status}
                    hideText={true}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div>
          <p>
            Page {offset + 1} of {totalPages}, showing {dataSource.length}{" "}
            record
            {dataSource.length === 1 ? "" : "s"} out of {totalShifts}
          </p>
          <div>
            <PaginationControl
              page={offset + 1}
              between={3}
              total={totalShifts}
              limit={fetchCount}
              changePage={(page) => {
                setOffset(page - 1);
              }}
              ellipsis={1}
            />
          </div>
        </div>
      </Fragment>
    );
  };

  return (
    <div>
      {needTitle ? (
        <Row>
          <PageTitleController
            title={"Timesheets"}
            icon={<BsCalendar3RangeFill className={"icon-right-spacing"} />}
          />
          {hasPermission ? (
            <Col>
              <Row xs={"auto"} className={"justify-content-lg-end"}>
                {isActiveTable(key) ? (
                  <ButtonBulkDelete action={() => setShowBulkDelete(true)} />
                ) : null}
                {isApprovedTab() && selectedCount > 0 ? (
                  <Col className={"g-3"}>
                    <Button
                      className={"shift-button-approve icon-text-wrapper"}
                      onClick={bulkCreateInvoices}
                      disabled={bulkCreateLoading}
                    >
                      {bulkCreateLoading && bulkCreateProgress ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-1" role="status" />
                          {bulkCreateProgress.current} of {bulkCreateProgress.total}
                        </>
                      ) : "Create Invoices"}
                    </Button>
                  </Col>
                ) : null}
                <Col className={"g-3"}>
                  <Button
                    as={Link}
                    to={"/reports"}
                    state={{ redirectKey: "timesheet-weeks" }}
                    className={"shift-button-new-shift icon-text-wrapper"}
                  >
                    <BsFillClipboard2DataFill
                      className={"icon-right-spacing"}
                    />{" "}
                    {"Timesheet Folders"}
                  </Button>
                </Col>
              </Row>
            </Col>
          ) : null}
        </Row>
      ) : null}

      {needTabs ? (
        <Tabs
          id="shifts-table-tabs"
          activeKey={key}
          onSelect={(k) => {
            setOffset(0);
            setKey(k);
          }}
          className="mb-3"
        >
          <Tab eventKey="ALL" title="All"></Tab>
          <Tab eventKey="MISSING" title="Missing"></Tab>
          <Tab eventKey="PENDING" title="Pending"></Tab>
          <Tab eventKey="REJECTED" title="Rejected"></Tab>
          <Tab eventKey="APPROVED" title="Approved"></Tab>
        </Tabs>
      ) : null}

      <Container fluid className={"p-0 m-0"}>
        <Row xs={"auto"} className={"p-0 m-0"}>
          <Col xs={"auto"} className={"ps-0"}>
            <DropDownTimesheetWeekSelection
              ref={timesheetWeekRef}
              onSelected={setTimesheetWeek}
              defaultSelect={timesheetWeek}
              placeholderText="Select Week"
            />
          </Col>
          <Col xs={"auto"}>
            <UserSelection
              ref={userRef}
              onSelected={setUser}
              defaultValue={user}
              disabled={defaultUser !== null}
              placeholderText="Select User"
            />
          </Col>
          <Col xs={"auto"}>
            <DropDownTrustSelection
              ref={trustRef}
              onSelected={setTrust}
              defaultValue={trust}
              placeholderText="Select Trust"
            />
          </Col>
          <Col xs={"auto"}>
            <DropDownRoleSelection
              ref={roleRef}
              onSelected={setRole}
              defaultValue={role}
              placeholderText="Select Role"
            />
          </Col>
          <Col xs={"auto"}>
            <DropDownFetchCount
              ref={fetchCountRef}
              onSelected={setFetchCount}
              defaultSelection={fetchCount}
              placeholderText="Select Rows"
            />
          </Col>
          <Col xs={"auto"}>
            <Button
              variant={"danger"}
              className={"clear-filters-btn"}
              onClick={() => {
                setDisableClear((prevState) => !prevState);
                clearFilters();
              }}
              disabled={disableClear}
            >
              <strong>Clear Filters</strong>
            </Button>
          </Col>
        </Row>
      </Container>

      <br />

      {SpinnerOrData(loading)}
      <BulkDeleteModal
        visible={showBulkDelete}
        close={() => {
          setShowBulkDelete(false);
          paginateShifts();
        }}
        shifts={dataSource}
        isArchive={true}
      />
      <ShiftTableDetailModal
        shift={selectDetail}
        close={closeDetailModal}
        visible={detailModalVisible}
      />
    </div>
  );
};

export default TimesheetTable;
